/**
 * DcMontagesBlock — редакция монтажей в подвкладке «Газета» (DC → Юнифанс).
 * Очередь заявок от игроков (бот принимает видео/ссылку), просмотр, подпись,
 * публикация или отказ с причиной. Публикуется в рубрику «Снято гостями».
 */
import { useCallback, useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { api } from "../../api/client";
import { NUM, box, capTitle, fmt } from "./dcVideoStatsParts";

export interface MontageItem {
  id: number; tg: number; author: string; username: string;
  kind: string; url: string; caption: string; title: string; durationSec: number;
  status: "new" | "approved" | "rejected" | "published";
  rejectReason: string; reviewedBy: string; reviewedAt: string;
  createdAt: string; streamUrl: string;
}

const STATUS_RU: Record<MontageItem["status"], string> = {
  new: "Новые", approved: "Одобрено", rejected: "Отклонено", published: "В газете",
};

export function DcMontagesBlock({ onToast }: { onToast?: (m: string) => void }): ReactElement {
  const [items, setItems] = useState<MontageItem[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState<MontageItem["status"] | "all">("new");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [reason, setReason] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api<{ items: MontageItem[]; counts: Record<string, number> }>(`/api/commerce/montages?status=${filter}`)
      .then((d) => { setItems(d.items ?? []); setCounts(d.counts ?? {}); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id: number, body: Record<string, unknown>, msg: string): Promise<void> => {
    setBusy(true);
    try {
      await api<{ ok: true }>(`/api/commerce/montages/${id}`, { method: "PATCH", body });
      onToast?.(msg);
      load();
    } catch (e) {
      onToast?.(e instanceof Error ? e.message : "Не удалось сохранить");
    } finally { setBusy(false); }
  };

  const seg = (on: boolean): CSSProperties => ({ padding: "3px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", border: "1px solid " + (on ? "var(--purple)" : "var(--surface-3)"), background: on ? "color-mix(in srgb, var(--purple) 18%, transparent)" : "transparent", color: on ? "var(--text-1)" : "var(--text-2)" });
  const act = (bg: string): CSSProperties => ({ flex: 1, padding: "5px 6px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: busy ? "wait" : "pointer", border: "1px solid " + bg, background: `color-mix(in srgb, ${bg} 16%, transparent)`, color: "var(--text-1)" });
  const dur = (s: number): string => s > 0 ? `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}` : "—";

  // Публикация ручная (Codex + деплой): отдаём готовую справку под промпт.
  const copySpec = async (m: MontageItem): Promise<void> => {
    const lines = [
      `Монтаж #${m.id}`,
      `Автор: ${m.author || "—"}${m.username ? ` (@${m.username})` : ""}`,
      `telegram_id автора (для доната): ${m.tg}`,
      m.title ? `Заголовок: ${m.title}` : "",
      m.caption ? `Подпись: ${m.caption}` : "",
      m.kind === "video" ? `Длительность: ${dur(m.durationSec)}` : "",
      m.kind === "video" ? `Файл (нужна авторизация стаффа): ${m.streamUrl}` : `Ссылка: ${m.url}`,
      `Прислан: ${m.createdAt.slice(0, 16).replace("T", " ")}`,
    ].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      onToast?.("Справка скопирована — вставляй в промпт");
    } catch {
      onToast?.("Не удалось скопировать");
    }
  };

  return (
    <div style={{ ...box, padding: 8, display: "grid", gap: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <span style={capTitle}>Монтажи · редакция</span>
        <span style={{ ...NUM, fontSize: 10, color: "var(--text-3)" }}>новых {fmt(counts.new ?? 0)} · в газете {fmt(counts.published ?? 0)}</span>
      </div>

      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {(["new", "approved", "published", "rejected", "all"] as const).map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} style={seg(filter === f)}>
            {f === "all" ? "Все" : STATUS_RU[f]}{f !== "all" && counts[f] ? ` ${counts[f]}` : ""}
          </button>
        ))}
      </div>

      {loading ? <div style={{ padding: 10, color: "var(--text-3)", fontSize: 12 }}>Загрузка…</div>
        : items.length === 0 ? <div style={{ padding: 10, color: "var(--text-3)", fontSize: 12 }}>Заявок нет</div>
          : items.map((m, i) => (
            <div key={m.id} style={{ borderTop: i ? "1px solid var(--surface-3)" : "none", paddingTop: i ? 6 : 0, display: "grid", gap: 4 }}>
              <button type="button" onClick={() => setOpenId(openId === m.id ? null : m.id)}
                style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center", width: "100%", minHeight: 30, padding: "2px 0", border: "none", background: "transparent", color: "var(--text-1)", cursor: "pointer", textAlign: "left" }}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 12.5, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.author || (m.username ? "@" + m.username : "id " + m.tg)}
                  </span>
                  <span style={{ ...NUM, fontSize: 9.5, color: "var(--text-3)" }}>
                    {m.kind === "video" ? `видео ${dur(m.durationSec)}` : "ссылка"} · {m.createdAt.slice(0, 16).replace("T", " ")}
                  </span>
                </span>
                <span style={{ ...NUM, fontSize: 10, color: m.status === "published" ? "var(--role)" : m.status === "rejected" ? "var(--red)" : "var(--text-2)" }}>
                  {STATUS_RU[m.status]}
                </span>
              </button>

              {openId === m.id ? (
                <div style={{ display: "grid", gap: 6, paddingBottom: 4 }}>
                  {m.kind === "video" && m.streamUrl
                    ? <video controls preload="metadata" src={m.streamUrl} style={{ width: "100%", maxHeight: "46dvh", borderRadius: 8, background: "#000" }} />
                    : m.url ? <a href={m.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "var(--role)", wordBreak: "break-all" }}>{m.url}</a> : null}
                  {m.caption ? <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.4 }}>{m.caption}</div> : null}
                  <div style={{ display: "flex", gap: 6 }}>
                    <button type="button" style={{ ...act("var(--surface-3)"), color: "var(--text-2)" }} onClick={() => { void copySpec(m); }}>Справка для промпта</button>
                    {m.kind === "video" && m.streamUrl
                      ? <a href={m.streamUrl} download={`montage-${m.id}.mp4`} style={{ ...act("var(--surface-3)"), color: "var(--text-2)", textAlign: "center", textDecoration: "none" }}>Скачать файл</a>
                      : null}
                  </div>
                  {m.rejectReason ? <div style={{ fontSize: 11, color: "var(--red)" }}>Отказ: {m.rejectReason}</div> : null}
                  <div style={{ display: "flex", gap: 6 }}>
                    <button type="button" disabled={busy} style={act("var(--role)")}
                      onClick={() => patch(m.id, { status: "published" }, "Опубликовано в газете")}>В газету</button>
                    <button type="button" disabled={busy} style={act("var(--purple)")}
                      onClick={() => patch(m.id, { status: "approved" }, "Одобрено")}>Одобрить</button>
                    <button type="button" disabled={busy} style={act("var(--red)")}
                      onClick={() => { setRejectId(rejectId === m.id ? null : m.id); setReason(""); }}>Отклонить</button>
                  </div>
                  {rejectId === m.id ? (
                    <div style={{ display: "flex", gap: 6 }}>
                      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Причина отказа (видит только редакция)"
                        style={{ flex: 1, minWidth: 0, padding: "5px 8px", borderRadius: 6, border: "1px solid var(--surface-3)", background: "var(--surface-2)", color: "var(--text-1)", fontSize: 12 }} />
                      <button type="button" disabled={busy || !reason.trim()} style={{ ...act("var(--red)"), flex: "0 0 auto", padding: "5px 10px" }}
                        onClick={() => { void patch(m.id, { status: "rejected", rejectReason: reason }, "Отклонено"); setRejectId(null); }}>ОК</button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
    </div>
  );
}
